import { Button, Alert, Form, Input, Modal } from 'antd-v4'
import { inject } from 'mobx-react'
import React, { Dispatch, FC, SetStateAction, useState } from 'react'
import { configureMerrco, IPaymentIntegration } from '@getgreenline/blaze-ecomm'
import './styles.scss'
import Endpoints from '../../../../constants/Endpoints'
import { parseErrorMsg } from '../../../../utilities/helpers'
import axios from 'axios'
import { LocationWithConfig } from './EcommerceSettingModels'

interface IMerrcoDetailsForm {
  clientId: string
  clientSecret: string
  publicKey: string
}

interface IConnectMerrcoModalProps {
  visible: boolean
  closeModal: () => void
  setConnectMerrcoMessage: Dispatch<SetStateAction<string | null>>
  setSelectedLocation: Dispatch<SetStateAction<LocationWithConfig | null>>
  selectedLocation: LocationWithConfig
  merrcoIntegrationData: IPaymentIntegration | undefined
}

export const ConnectMerrcoModal: FC<IConnectMerrcoModalProps> = inject('currentCompanyStore')(
  ({
    visible,
    closeModal,
    setConnectMerrcoMessage,
    setSelectedLocation,
    selectedLocation,
    merrcoIntegrationData,
  }: IConnectMerrcoModalProps) => {
    const [form] = Form.useForm()

    const [isConfigureMerrcoLoading, setIsConfigureMerrcoLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const clientId = merrcoIntegrationData ? merrcoIntegrationData.attributes.clientId : ''
    const publicKey = merrcoIntegrationData ? merrcoIntegrationData.attributes.publicKey : ''
    const clientSecret = merrcoIntegrationData ? merrcoIntegrationData.attributes.clientSecret : ''

    const handleConfirm = async (formValues: unknown) => {
      setIsConfigureMerrcoLoading(true)
      const merrcoDetails = formValues as IMerrcoDetailsForm
      try {
        await configureMerrco(Endpoints.AUTHZ_GATEWAY_URL, {
          tenantId: selectedLocation.companyId.toString(),
          retailerId: selectedLocation.id.toString(),
          paymentOptions: {
            merrco: {
              publicKey: merrcoDetails.publicKey,
              clientId: merrcoDetails.clientId,
              clientSecret: merrcoDetails.clientSecret,
            },
          },
        })
        setIsConfigureMerrcoLoading(false)
        closeModal()
        setConnectMerrcoMessage('Merrco was successfully connected to your store.')
        setSelectedLocation(null)
      } catch (error) {
        setIsConfigureMerrcoLoading(false)
        if (axios.isAxiosError(error)) {
          const errorResponse = JSON.parse(error.response?.data.error)
          if (errorResponse.errors[0]) {
            const errorMessage = errorResponse.errors[0].detail
            setErrorMessage(errorMessage)
          } else {
            setErrorMessage('An error occurred while configuring Merrco.')
          }
        } else {
          setErrorMessage(parseErrorMsg(error) || 'An error occurred while configuring Merrco.')
        }
      }
    }

    const handleCancel = () => {
      form.resetFields()
      setErrorMessage('')
      closeModal()
    }

    return (
      <Modal
        zIndex={1001}
        title={'Connect to Merrco'}
        visible={visible}
        onCancel={handleCancel}
        footer={[
          <Button key='cancel' onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key='submit'
            form='connect-merrco'
            htmlType='submit'
            type='primary'
            loading={isConfigureMerrcoLoading}
          >
            Connect
          </Button>,
        ]}
      >
        <Form id='connect-merrco' form={form} onFinish={handleConfirm}>
          <Form.Item
            name='clientId'
            label='Client ID'
            style={{ marginBottom: '0.5em' }}
            rules={[{ required: true, message: 'Please enter the Client ID' }]}
            initialValue={clientId}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name='clientSecret'
            label='Client Secret'
            style={{ marginBottom: '0.5em' }}
            rules={[{ required: true, message: 'Please enter the Client Secret' }]}
            initialValue={clientSecret}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name='publicKey'
            label='Public Key'
            rules={[{ required: true, message: 'Please enter the Public Key' }]}
            initialValue={publicKey}
          >
            <Input />
          </Form.Item>
        </Form>
        {errorMessage && (
          <div className='mt-3'>
            <Alert type='error' message={errorMessage} />
          </div>
        )}
      </Modal>
    )
  },
)
